import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import { setUser, setAccessToken } from "../../store/authSlice";
import OtpInput from "react-otp-input";
import { AppButton } from "../../components/buttons/button";
import { H1Title, Paragraph } from "../../components/typography/typography";
import AuthService from "../../services/authService";
import { Success, Error as ToastError } from "../../components/toast/toast";

export default function Verify() {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const email = useSelector((state) => state.auth.email);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await AuthService.verifyOTP({ email, otp });
      dispatch(setUser(data.data.user));
      dispatch(setAccessToken(data.data.accessToken));
      Sentry.setUser({ id: data.data.user._id, email: data.data.user.email, role: data.data.user.role });
      Success('Email verified!');
      navigate('/dashboard');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await AuthService.resendOTP({ email });
      Success('New code sent!');
    } catch (err) {
      ToastError('Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <form onSubmit={handleVerify} className="w-full space-y-6">
      <div>
        <H1Title size="sm">Verify Your Email</H1Title>
        <Paragraph size="base" className="text-grey-667 mt-1">
          We sent a 6-digit code to <span className="font-semibold text-black">{email}</span>
        </Paragraph>
      </div>

      <div className="flex justify-center">
        <OtpInput
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderInput={(props) => (
            <input {...props}
              className="!w-12 !h-12 mx-1 text-center text-lg font-semibold border border-grey-200 rounded-md outline-none focus:border-primary"
            />
          )}
          shouldAutoFocus
        />
      </div>

      <AppButton type="submit" loading={loading} disabled={otp.length !== 6}>
        Verify Email
      </AppButton>

      <p className="text-sm text-center text-grey-667">
        Didn't receive a code?{' '}
        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="text-primary font-semibold hover:underline cursor-pointer"
        >
          {resending ? 'Sending...' : 'Resend'}
        </button>
      </p>
    </form>
  );
}
